<template>
  <div class="editPharmacy">
    <Loading v-if="loading" />
    <Title :title="$t('pharmacyInformation')" />
    <FormKit
      id="pharmacyForm"
      v-model="formData"
      type="form"
      :actions="false"
      @submit="submitHandler"
    >
      <div class="editPharmacy__container">
        <div class="editPharmacy__generalInfo">
          <div class="editPharmacy-logo">
            <div class="container">
              <img
                class="editPharmacyLogo"
                :src="pharmacy?.pictureUrl"
                alt="Logo"
              />
              <div class="overlay">
                <div class="overlay-content">
                  <Photo @change="onFileChange" />
                </div>
              </div>
            </div>
          </div>
          <Input
            v-for="el in infoOfPharmacy"
            :key="el.id"
            :type="el.type"
            :name="el.name"
            :label="$t(el.label)"
            :validation="el.validation"
            :validation-messages="{
              required: $t('validation.required')
            }"
            :disabled="disabledInput(el.name)"
            :data-testid="el.dataTestId"
          />
          <div class="editPharmacy-description">
            <label class="label-description" for="description">
              {{ $t('description') }}
            </label>
            <textarea
              id="description"
              v-model="formData.description"
              class="textarea-description"
              maxlength="250"
            >
            </textarea>
          </div>
          <Dropdown
            class="infoOfPharmacy-dropdown"
            :selected-option="langsDropdown.selectedOption"
            :options="langsDropdown.options"
            :label="$t('chooseLanguage')"
            :multiple="getMultiple()"
            @select-option="selectLanguage"
          />

          <div v-for="el in infoOfDeliveries" :key="el.id" class="block-input">
            <Input
              :type="el.type"
              :name="el.name"
              :label="$t(el.label)"
              :disabled="disabledInput(el.name)"
              :data-testid="el.dataTestId"
            />
            <span>
              {{ $t('daysDelivery') }}
            </span>
          </div>
          <Input
            v-for="el in infoEmail"
            :key="el.id"
            :type="el.type"
            :name="el.name"
            :label="$t(el.label)"
            :validation="el.validation"
            :validation-messages="{
              required: $t('validation.required'),
              email: $t('validation.email')
            }"
            :data-testid="el.dataTestId"
          />
          <FormKit type="group" name="additionalEmails">
            <Input
              v-for="(el, idx) in emailItems"
              :id="el"
              :key="idx"
              :name="el"
              :label="$t('alarmEmail')"
              :validation="'required|email'"
              :validation-messages="{
                required: $t('validation.required'),
                email: $t('validation.email')
              }"
              :disabled="!user?.role.permissions.pharmacy.update"
              :data-testid="'inputAddAlarmEmail' + idx"
              @close-input="removeEmailItem"
            />
          </FormKit>
          <AddInfo :title="$t('addEmail')" @click="addEmailItem" />
          <Input
            v-for="el in infoPhoneNumber"
            :key="el.id"
            :type="el.type"
            :name="el.name"
            :label="$t(el.label)"
            :validation="el.validation"
            :validation-visibility="el.validationVisibility"
            :validation-messages="{
              matches: $t('validation.phoneNumber'),
              required: $t('validation.required')
            }"
            :disabled="!user?.role.permissions.pharmacy.update"
            :data-testid="el.dataTestId"
          />
          <FormKit type="group" name="additionalPhoneNumbers">
            <Input
              v-for="(el, idx) in phoneItems"
              :id="el"
              :key="idx"
              :name="el"
              :label="$t('alarmPhoneNumber')"
              :validation="[['required'], ['matches', /^\+[0-9]{10,14}$/]]"
              validation-visibility="dirty"
              :validation-messages="{
                matches: $t('validation.phoneNumber'),
                required: $t('validation.required')
              }"
              :disabled="!user?.role.permissions.pharmacy.update"
              :data-testid="'inputAddAlarmPhoneNumber' + idx"
              @close-input="removePhoneItem"
            />
          </FormKit>
          <AddInfo :title="$t('addPhoneNumber')" @click="addPhoneItem" />
        </div>
        <div
          v-if="pharmacy?.remomediServices.mobileApp"
          class="editPharmacy__moreInfo"
        >
          <div class="workingHours">
            <div class="workingHours__title">
              <p class="title">
                {{ $t('workingHours') }}
              </p>
            </div>
            <div
              v-for="(workingHours, day) in formData.workingHours"
              :key="day"
              class="workingHours__scheduleWork"
            >
              <p v-if="!workingHours.closed" class="day">
                {{ getFullDay(day) }}
              </p>
              <span v-if="!workingHours.closed" class="hours">
                {{ getWorkingHours(workingHours?.from, workingHours?.to) }}
              </span>
            </div>
            <div
              class="workingHours__editWorkingHours"
              @click="editWorkingHours"
            >
              <img class="icon" src="@/assets/icons/EditGreen.svg" alt="Edit" />
              <span class="editWorkingHours-title">{{ $t('edit') }}</span>
            </div>
          </div>
          <div class="services">
            <div class="services__title">
              <p class="title">
                {{ $t('services') }}
              </p>
            </div>
            <FormKit type="group" name="services">
              <div class="services__types">
                <Checkbox
                  name="pickupbox"
                  :value="pharmacy?.services.pickupbox"
                  :title="$t('pickupBox')"
                  @change="changePickupbox"
                />
                <Checkbox
                  name="homeDelivery"
                  :value="pharmacy?.services.homeDelivery"
                  :title="$t('homeDelivery')"
                  @change="changeHomeDelivery"
                />
                <Checkbox
                  name="pharmacyPickup"
                  :value="pharmacy?.services.pharmacyPickup"
                  :title="$t('pharmacyPickup')"
                  @change="changePharmacyPickup"
                />
              </div>
            </FormKit>
          </div>
          <div class="showToCustomers">
            <div class="showToCustomers__title">
              <p class="title">
                {{ $t('showToCustomersUpperCase') }}
              </p>
            </div>
            <div class="showToCustomers__checkbox">
              <Checkbox
                name="showToCustomers"
                :value="pharmacy?.showToCustomers"
                :title="$t('showToCustomers')"
                @change="changeShowToCustomers"
              />
            </div>
            <Checkbox
              name="sendNewCallbackRequestEmailNotification"
              :value="pharmacy?.sendNewCallbackRequestEmailNotification"
              :title="$t('sendNewCallbackRequestEmailNotification')"
              @change="changeSendNewCallbackRequestEmailNotification"
            />
            <div class="remomediServices__checkbox">
              <Checkbox
                name="callbacks"
                :value="
                  pharmacy?.callbacks || pharmacy?.remomediServices.callbacks
                "
                :title="$t('callbacks')"
                @change="changeCallbacks"
              />
            </div>
          </div>
          <Input
            class="input-phoneNumber"
            type="tel"
            name="customersPhoneNumber"
            :label="$t('customerPhoneNumber')"
            :validation="[['required'], ['matches', /^\+[0-9]{10,14}$/]]"
            validation-visibility="dirty"
            :validation-messages="{
              matches: $t('validation.phoneNumber'),
              required: $t('validation.required')
            }"
            :disabled="!user?.role.permissions.pharmacy.update"
          />
          <Dropdown
            v-if="pharmacy?.remomediServices.mobileApp"
            class="infoOfPharmacy-dropdown"
            :selected-option="serviceLangsDropdown.selectedOption"
            :options="serviceLangsDropdown.options"
            :label="$t('serviceLanguagesAvailable')"
            :multiple="true"
            @select-option="selectServiceLanguage"
          />
        </div>
      </div>
    </FormKit>
  </div>
  <Footer
    :disabled-make-service-request="formData.serviceRequest"
    @clean-form="cleanForm"
    @submit-form="submitForm"
    @open-modal-service-request="openModal"
  />
  <ServiceRequest
    :show-modal-service-request="showModalServiceRequest"
    @close-modal="showModalServiceRequest = false"
    @save-service-request="saveServiceRequest"
  />
  <EditWorkingHours
    :show-modal-edit-working-hours="showModalEditWorkingHours"
    :pharmacy-working-hours="formData?.workingHours"
    @close-modal="closeModalEditWorkingHour"
    @close-modal-old="closeModalOldWorkingHour"
  />
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';
import { token } from '@formkit/utils';

import Loading from '@/views/Loading.vue';
import ServiceRequest from './ServiceRequest/ServiceRequest.vue';
import EditWorkingHours from './EditWorkingHours/EditWorkingHours.vue';

import {
  infoOfPharmacy,
  infoPhoneNumber,
  infoEmail,
  infoOfDeliveries,
  formDataDefault
} from './configs';
import {
  optionsLanguageEn,
  optionsLanguageFi,
  optionsLanguageSv,
  optionsLanguageNo,
  optionsLanguageLv,
  optionsLanguageSl,
  langsEn,
  langsFi,
  langsSv,
  langsNo,
  langsLv,
  langsSl
} from '@/services/languageService';

import { allLanguages } from './serviceLanguages';

import { Option } from '@/types';
import { DataServiceRequest } from '@/store/modules/pharmacy/types';

@Options({
  name: 'EditPharmacy',
  emits: ['saveServiceRequest', 'saveWorkingHours'],
  components: {
    Loading,
    ServiceRequest,
    EditWorkingHours
  },
  data() {
    return {
      formData: formDataDefault,
      payload: null,
      localPharmacyWorkingHours: null,
      emailItems: [],
      phoneItems: [],
      infoOfPharmacy,
      infoPhoneNumber,
      infoEmail,
      infoOfDeliveries,
      langsEn,
      langsFi,
      langsSv,
      langsNo,
      langsLv,
      allLanguages,
      langsDropdown: {
        selectedOption: [],
        options: []
      },
      serviceLangsDropdown: {
        selectedOption: [],
        options: []
      },
      loading: false,
      updatedPharmacyLogo: false,
      showModalServiceRequest: false,
      showModalEditWorkingHours: false
    };
  },
  created() {
    this.show();
  },
  computed: {
    ...mapState('pharmacy', ['pharmacy']),
    ...mapState('account', ['user'])
  },
  methods: {
    /* eslint-disable */
    ...mapActions('pharmacy', [
      'getPharmacy',
      'getPharmacyServiceRequest',
      'updatePharmacy',
      'updatePharmacyLogo'
    ]),
    async show() {
      const { id } = this.$route.params;
      this.loading = true;

      await this.getPharmacy(id);

      if (this.pharmacy?.workingHours) {
        this.localPharmacyWorkingHours = JSON.parse(
          JSON.stringify(this.pharmacy?.workingHours)
        );
      }

      this.fillForm();

      this.loading = false;
    },
    getMultiple() {
      return !this.formData?.pickupBox?.customerSMSLanguage;
    },
    getFullDay(abbreviatedDay: string) {
      const daysMap: Record<string, unknown> = {
        mon: this.$t('monday'),
        tue: this.$t('tuesday'),
        wed: this.$t('wednesday'),
        thu: this.$t('thursday'),
        fri: this.$t('friday'),
        sat: this.$t('saturday'),
        sun: this.$t('sunday')
      };

      return daysMap[abbreviatedDay];
    },
    getWorkingHours(hourFrom: string, hourTo: string) {
      if (hourFrom) {
        return `${hourFrom} - ${hourTo}`;
      } else {
        return this.$t('closed');
      }
    },
    onFileChange(event: Event) {
      const { id } = this.$route.params;

      const file = (event.target as HTMLInputElement).files?.[0];

      if (file) {
        const reader = new FileReader();

        reader.onload = async (e) => {
          this.imageSrc = e.target?.result as string;

          const payload = {
            logo: this.imageSrc
          };
          try {
            this.loading = true;

            await this.updatePharmacyLogo({
              data: payload,
              pharmacyId: id
            });

            this.updatedPharmacyLogo = true;

            this.loading = false;
          } catch (error) {
            this.updatedPharmacyLogo = false;
            console.error(error);
          }
        };

        reader.readAsDataURL(file);
      }
    },
    initLanguage() {
      const optionsMappings: { [key: string]: Option[] } = {
        en: optionsLanguageEn,
        fi: optionsLanguageFi,
        sv: optionsLanguageSv,
        no: optionsLanguageNo,
        lv: optionsLanguageLv,
        sl: optionsLanguageSl
      };

      const userLanguage = this.user?.appLanguage;

      if (userLanguage && optionsMappings[userLanguage]) {
        this.langsDropdown.options = optionsMappings[userLanguage];
      }
    },
    initServiceLanguage() {
      const serviceLangs = Object.entries(this.allLanguages).map(
        ([code, name]) => ({
          label: name,
          value: code.toLowerCase()
        })
      );

      this.serviceLangsDropdown.options = serviceLangs;
    },
    changePickupbox() {
      this.pharmacy.services.pickupbox = !this.pharmacy.services.pickupbox;
    },
    changeHomeDelivery() {
      this.pharmacy.services.homeDelivery =
        !this.pharmacy.services.homeDelivery;
    },
    changePharmacyPickup() {
      this.pharmacy.services.pharmacyPickup =
        !this.pharmacy.services.pharmacyPickup;
    },
    changeShowToCustomers() {
      this.pharmacy.showToCustomers = !this.pharmacy.showToCustomers;
    },
    changeCallbacks() {
      this.pharmacy.callbacks = !this.pharmacy.callbacks;
    },
    changeSendNewCallbackRequestEmailNotification() {
      this.pharmacy.sendNewCallbackRequestEmailNotification =
        !this.pharmacy.sendNewCallbackRequestEmailNotification;
    },
    fillAdditionalFields(items: string[], field: string, itemName: string) {
      items.length = 0;
      this.formData[field] = {};
      const length = this.pharmacy[field]?.length;

      for (let i = 0; i < length; i++) {
        const item = `${itemName}${token()}`;
        items.push(item);
        this.formData[field][item] = this.pharmacy[field][i];
      }
    },
    fillForm() {
      Object.keys(this.pharmacy).forEach((field) => {
        switch (field) {
          case 'additionalEmails': {
            this.fillAdditionalFields(this.emailItems, field, 'email');
            break;
          }
          case 'additionalPhoneNumbers': {
            this.fillAdditionalFields(this.phoneItems, field, 'phone');
            break;
          }
          default: {
            this.formData[field] = this.pharmacy[field];
            break;
          }
        }
      });
    },
    disabledInput(name: string) {
      if (
        name === 'pinCode' ||
        name === 'deliveryKeepTime' ||
        name === 'deliveryReminderMessagePeriod' ||
        name === 'streetAddress' ||
        !this.user?.role.permissions.pharmacy.update
      ) {
        return true;
      } else {
        false;
      }
    },
    async submitHandler(data: any) {
      this.loading = true;
      const { id } = this.$route.params;

      if (data.pickupBox.customerSMSLanguage) {
        this.payload = {
          ...data,
          additionalEmails: Object.values(data.additionalEmails),
          additionalPhoneNumbers: Object.values(data.additionalPhoneNumbers),
          pickupBox: {
            ...data.pickupBox,
            customerSMSLanguage: this.formData.pickupBox.customerSMSLanguage
          }
        };
      } else {
        this.payload = {
          ...data,
          additionalEmails: Object.values(data.additionalEmails),
          additionalPhoneNumbers: Object.values(data.additionalPhoneNumbers),
          pickupBox: {
            ...data.pickupBox,
            customerSMSLanguages: this.formData.pickupBox.customerSMSLanguages
          }
        };
      }

      delete this.payload.checkbox_4;

      await this.updatePharmacy({
        data: this.payload,
        pharmacyId: id
      });

      await this.getPharmacy(id);

      this.loading = false;
    },
    openModal() {
      if (!this.formData.serviceRequest) {
        this.showModalServiceRequest = true;
      }
    },
    editWorkingHours() {
      this.showModalEditWorkingHours = true;
    },
    async saveServiceRequest(data: DataServiceRequest) {
      this.loading = true;
      const { id } = this.$route.params;

      const payload = {
        serviceRequest: !data.serviceRequest,
        description: data.description
      };

      await this.getPharmacyServiceRequest({
        data: payload,
        pharmacyId: id
      });

      await this.getPharmacy(id);
      this.formData = {
        ...this.pharmacy
      };

      this.showModalServiceRequest = false;
      this.loading = false;
    },
    selectLanguage(option: Option) {
      this.langsDropdown.selectedOption = option;
    },
    selectServiceLanguage(option: Option) {
      this.serviceLangsDropdown.selectedOption = option;
    },
    addEmailItem() {
      if (this.user?.role.permissions.pharmacy.update) {
        this.emailItems.push(`email${token()}`);
      }
    },
    removeEmailItem(name: string) {
      this.emailItems = this.emailItems.filter((item: string) => item !== name);
      delete this.formData.additionalEmails[name];
    },
    addPhoneItem() {
      if (this.user?.role.permissions.pharmacy.update) {
        this.phoneItems.push(`phone${token()}`);
      }
    },
    removePhoneItem(name: string) {
      this.phoneItems = this.phoneItems.filter((item: string) => item !== name);
      delete this.formData.additionalPhoneNumbers[name];
    },
    closeModalEditWorkingHour() {
      this.showModalEditWorkingHours = false;
    },
    closeModalOldWorkingHour() {
      this.formData.workingHours = this.localPharmacyWorkingHours;
      this.showModalEditWorkingHours = false;
    },
    submitForm() {
      this.$formkit.submit('pharmacyForm');
    },
    cleanForm() {
      this.formData.name = '';
      this.formData.email = '';
      this.formData.additionalEmails = {};
      this.formData.customersPhoneNumber = '';
      this.formData.additionalPhoneNumbers = {};
    },
    initServiceLanguagesPharmacy() {
      const serviceLanguagesPharmacy = this.pharmacy?.serviceLanguages;
      const serviceLangs = Object.entries(this.allLanguages).map(
        ([code, name]) => ({
          label: name,
          value: code.toLowerCase()
        })
      );

      const serviceLanguagesResult = serviceLangs.filter((lang: any) =>
        serviceLanguagesPharmacy.includes(lang.value)
      );

      this.serviceLangsDropdown.selectedOption = serviceLanguagesResult;
    }
  },
  watch: {
    'langsDropdown.selectedOption': {
      immediate: true,
      handler() {
        const language = this.pharmacy?.pickupBox.customerSMSLanguage;

        if (language) {
          this.formData.pickupBox.customerSMSLanguage =
            this.langsDropdown.selectedOption.value || '';
          delete this.formData.pickupBox.customerSMSLanguages;
        } else {
          const languagesValues = this.langsDropdown.selectedOption.map(
            (i: Option) => i.value
          );

          this.formData.pickupBox.customerSMSLanguages = languagesValues || '';
        }
      }
    },
    'serviceLangsDropdown.selectedOption': {
      immediate: true,
      handler() {
        this.formData.serviceLanguages =
          this.serviceLangsDropdown.selectedOption.map(
            (option: Option) => option.value
          );
      }
    },
    pharmacy() {
      if (this.pharmacy) {
        this.formData.customersPhoneNumber = this.pharmacy.customersPhoneNumber;

        this.initLanguage();
        this.initServiceLanguage();
        this.initServiceLanguagesPharmacy();

        const languages = this.pharmacy?.pickupBox.customerSMSLanguages;
        const language = this.pharmacy?.pickupBox.customerSMSLanguage;

        const languageMappings: { [key: string]: Record<string, unknown> } = {
          en: langsEn,
          fi: langsFi,
          sv: langsSv,
          no: langsNo,
          lv: langsLv,
          sl: langsSl
        };

        const userLanguage = this.user?.appLanguage;

        if (userLanguage && languageMappings[userLanguage]) {
          if (language) {
            const selectedOption = {
              label: languageMappings[userLanguage][language],
              value: language
            };

            this.langsDropdown.selectedOption = selectedOption;
          } else {
            const selectedOption = languages.map((i: string) => ({
              label: languageMappings[userLanguage][i],
              value: i
            }));

            this.langsDropdown.selectedOption = selectedOption;
          }
        }
      }
    },
    updatedPharmacyLogo() {
      if (this.updatedPharmacyLogo) {
        this.show();
      }
    }
  }
})
export default class EditPharmacy extends Vue {}
</script>

<style lang="scss">
.editPharmacy {
  padding: 24px;
  margin-bottom: 64px;
  overflow-y: scroll;
  .formkit-form {
    margin-top: 30px;
    .editPharmacy__container {
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
      .editPharmacy__generalInfo {
        .editPharmacy-logo {
          position: relative;
          width: 100px;
          height: 100px;
          border: 1px solid $line;
          border-radius: 50%;
          overflow: hidden;
          margin-bottom: 32px;
          .container {
            position: relative;
            width: 100px;
            height: 100px;
            border-radius: 50%;
            overflow: hidden;
            .editPharmacyLogo {
              width: 68%;
              height: 68%;
              object-fit: contain;
              margin-left: 16px;
              margin-top: 15px;
            }
            .overlay {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background-color: $backgroundLogo;
              transition: opacity 0.3s ease;
              border-radius: 50%;
              opacity: 0;
              &:hover {
                opacity: 1;
              }
              .overlay-content {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: $white;
                background-color: $backgroundImg;
                .photoInputOuter,
                .photoInputWrapper,
                .photoInputInner {
                  background-color: $backgroundImg;
                }
              }
            }
          }
          &:hover {
            border: 1px solid $backgroundImg;
          }
          &:hover .overlay {
            opacity: 1;
          }
        }
      }
      .editPharmacy__moreInfo {
        margin-left: 62px;
        .workingHours {
          margin-bottom: 47px;
          .workingHours__title {
            position: relative;
            margin-bottom: 26px;
            .title {
              font-family: 'Ubuntu-Regular';
              font-size: $sixteenSize;
              color: $servicesTitle;
            }
            &::after {
              content: '';
              position: absolute;
              top: 9px;
              left: 150px;
              width: 227px;
              height: 1px;
              background: $ligher-grey;
            }
            &::before {
              content: '';
              position: absolute;
              top: 7px;
              left: 145px;
              width: 5px;
              height: 5px;
              border-radius: 8px;
              background: $ligher-grey;
            }
          }
          .workingHours__scheduleWork {
            display: flex;
            justify-content: flex-start;
            align-items: center;
            width: 231px;
            .day {
              width: 148px;
              font-size: 14px;
              line-height: 20px;
              font-weight: 400;
            }
            .hours {
              font-size: 14px;
              line-height: 20px;
              color: $hours;
            }
          }
          .workingHours__editWorkingHours {
            display: flex;
            align-items: center;
            width: 53px;
            margin-top: 23px;
            cursor: pointer;
            .icon {
              width: 20px;
              height: 20px;
            }
            .editWorkingHours-title {
              font-family: 'Ubuntu-Medium';
              font-size: 14px;
              line-height: 20px;
              font-weight: 500;
              color: $main-green;
              margin-left: 9px;
            }
          }
        }
        .services {
          .services__title {
            position: relative;
            margin-bottom: 26px;
            .title {
              font-family: 'Ubuntu-Regular';
              font-size: $sixteenSize;
              color: $servicesTitle;
            }
            &::after {
              content: '';
              position: absolute;
              top: 9px;
              left: 85px;
              width: 292px;
              height: 1px;
              background: $ligher-grey;
            }
            &::before {
              content: '';
              position: absolute;
              top: 7px;
              left: 80px;
              width: 5px;
              height: 5px;
              border-radius: 8px;
              background: $ligher-grey;
            }
          }
          .services__types {
            .checkbox {
              margin-bottom: 16px;
            }
          }
        }
        .showToCustomers {
          margin-top: 47px;
          .showToCustomers__title {
            position: relative;
            margin-bottom: 26px;
            .title {
              font-family: 'Ubuntu-Regular';
              font-size: $sixteenSize;
              color: $servicesTitle;
            }
            &::after {
              content: '';
              position: absolute;
              top: 9px;
              left: 182px;
              width: 195px;
              height: 1px;
              background: $ligher-grey;
            }
            &::before {
              content: '';
              position: absolute;
              top: 7px;
              left: 177px;
              width: 5px;
              height: 5px;
              border-radius: 8px;
              background: $ligher-grey;
            }
          }
          .showToCustomers__checkbox {
            margin-bottom: 26px;
          }
          .remomediServices__checkbox {
            margin-top: 26px;
          }
        }
        .input-phoneNumber {
          margin-top: 47px;
        }
      }
    }
    .editPharmacy-description {
      position: relative;
      display: flex;
      justify-content: space-between;
      margin-bottom: 24px;
      .label-description {
        position: absolute;
        font-family: 'Ubuntu-Regular';
        top: -6px;
        left: 15px;
        padding: 0 5px;
        background: $white;
        color: $label;
        font-weight: 400;
        font-size: $twelveSize;
        line-height: $fourteenSize;
        z-index: 1;
      }
      .textarea-description {
        width: 345px;
        height: 140px;
        padding: 16px;
        font-size: $fourteenSize;
        border: 1px solid $border;
        border-radius: 8px;
        resize: none;
        &:focus {
          outline: none;
        }
      }
    }
    .block-input {
      position: relative;
      span {
        position: absolute;
        top: 18px;
        left: 330px;
        font-weight: 400;
        font-size: $fourteenSize;
        line-height: $fourteenSize;
      }
    }
  }
  .closeInput {
    display: none;
  }
}

.infoOfPharmacy-dropdown {
  height: 48px;
  width: 377px !important;
  margin-bottom: 30px;
}
</style>
